import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TablePagination,
  TableSortLabel,
  Typography,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import colors from '../theme/colors';
import EmptyState from './EmptyState';

/**
 * Reusable DataTable Component with modern UI
 * @param {Array} columns - Array of column definitions { field, headerName, width, render, sortable }
 * @param {Array} data - Array of data objects
 * @param {Function} onEdit - Edit handler
 * @param {Function} onDelete - Delete handler
 * @param {Function} onView - View handler
 * @param {Function} getRowId - Function to get unique row ID
 * @param {string} emptyMessage - Message when no data
 * @param {boolean} showActions - Show actions column
 * @param {boolean} disableActions - Disable actions column completely
 */

interface DataTableProps {
  columns?: any[];
  data?: any[];
  onEdit?: any;
  onDelete?: any;
  onView?: any;
  getRowId?: any;
  emptyMessage?: string;
  showActions?: boolean;
  disableActions?: boolean;
  idField?: string;
}

function DataTable({ 
  columns = [], 
  data = [], 
  onEdit, 
  onDelete,
  onView,
  getRowId,
  emptyMessage = 'No data available',
  showActions = true,
  disableActions = false,
  idField,
}: DataTableProps) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [orderBy, setOrderBy] = React.useState('');
  const [order, setOrder] = React.useState('asc');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedData = React.useMemo(() => {
    if (!orderBy) return data;
    return [...data].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, order, orderBy]);

  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleEdit = () => {
    if (onEdit && selectedRow) {
      onEdit(selectedRow);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (onDelete && selectedRow) {
      onDelete(selectedRow);
    }
    handleMenuClose();
  };

  const handleView = () => {
    if (onView && selectedRow) {
      onView(selectedRow);
    }
    handleMenuClose();
  };

  return (
    <>
      <Box
        sx={{
          borderRadius: 2,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
          backgroundColor: 'white',
        }}
      >
        <TableContainer 
          sx={{ 
            overflowX: 'auto',
            overflowY: 'hidden',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
        <Table size="medium" sx={{ minWidth: 1200 }}>
          <TableHead>
            <TableRow sx={{ 
              background: `linear-gradient(135deg, ${colors.table.headerGradientStart} 0%, ${colors.table.headerGradientEnd} 100%)`,
            }}>
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '0.6rem',
                    color: colors.table.headerText,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    borderBottom: '1px solid rgba(255,255,255,0.3)',
                    py: 0.6,
                    px: 1.2,
                    width: column.width,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.field}
                      direction={orderBy === column.field ? (order as 'asc' | 'desc') : 'asc'}
                      onClick={() => handleRequestSort(column.field)}
                      sx={{
                        color: `${colors.table.headerText} !important`,
                        '&:hover': { color: 'rgba(255,255,255,0.9) !important' },
                        '& .MuiTableSortLabel-icon': {
                          color: `${colors.table.headerText} !important`,
                        },
                        '&.Mui-active': {
                          color: `${colors.table.headerText} !important`,
                          '& .MuiTableSortLabel-icon': {
                            color: `${colors.table.headerText} !important`,
                          },
                        },
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {column.headerName}
                    </TableSortLabel>
                  ) : (
                    column.headerName
                  )}
                </TableCell>
              ))}
              {showActions && !disableActions && (onEdit || onDelete || onView) && (
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '0.6rem',
                    color: '#FFFFFF',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    borderBottom: '1px solid rgba(255,255,255,0.3)',
                    py: 0.6,
                    px: 1.2,
                    width: '70px',
                  }}
                >
                  
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell 
                  colSpan={columns.length + (showActions ? 1 : 0)} 
                  sx={{ py: 0, borderBottom: 'none', px: 0 }}
                >
                  <EmptyState message={emptyMessage} height={400} />
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, rowIndex) => (
                <TableRow 
                  key={getRowId ? getRowId(row) : (row.id || rowIndex)}
                  sx={{
                    backgroundColor: rowIndex % 2 === 0 ? colors.table.rowEven : colors.table.rowOdd,
                    '&:hover': {
                      backgroundColor: `${colors.table.rowHover} !important`,
                    },
                    '&:last-child td': {
                      borderBottom: 'none',
                    },
                  }}
                >
                  {columns.map((column, colIndex) => (
                    <TableCell 
                      key={colIndex} 
                      sx={{ 
                        py: 0.75,
                        px: 1.2,
                        fontSize: '0.7rem',
                        color: colors.text.primary,
                        borderBottom: `1px solid ${colors.table.border}`,
                      }}
                    >
                      {column.render ? column.render(row) : (
                        <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                          {row[column.field] || '-'}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                  {showActions && !disableActions && (onEdit || onDelete || onView) && (
                    <TableCell 
                      align="center" 
                      sx={{ 
                        py: 0.75,
                        px: 1.2,
                        borderBottom: `1px solid ${colors.table.border}`,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, row)}
                        sx={{
                          color: colors.primary.main,
                          '&:hover': {
                            backgroundColor: colors.action.hover,
                            color: colors.primary.light,
                          },
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {data.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: `2px solid ${colors.table.border}`,
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.8rem',
              color: '#6B7280',
            },
            '& .MuiTablePagination-select': {
              fontSize: '0.8rem',
            },
          }}
        />
      )}
    </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
            minWidth: 160,
          },
        }}
      >
        {onView && (
          <MenuItem onClick={handleView} sx={{ py: 1.5, fontSize: '0.875rem' }}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" sx={{ color: '#64748B' }} />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        )}
        {onEdit && (
          <MenuItem onClick={handleEdit} sx={{ py: 1.5, fontSize: '0.875rem' }}>
            <ListItemIcon>
              <EditIcon fontSize="small" sx={{ color: '#3B82F6' }} />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={handleDelete} sx={{ py: 1.5, fontSize: '0.875rem', color: '#EF4444' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: '#EF4444' }} />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

export default DataTable;
